import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Link, Loader2 } from 'lucide-react'
import {
  EventActivityPreviousSetup,
  getEventActivityPreviousSetups,
} from 'app/frontend/base/api/event'
import { useTeam } from 'app/frontend/base/config/context/team'
import dayjs from 'dayjs'
import { ReactNode, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { toast } from 'sonner'
import LinkButton from '@/components/ui/link-button'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'

type Props = {
  activity: wowaudit.EventActivity
  activities: wowaudit.EventActivity[]
  allSelected: boolean
  trigger: ReactNode
  extraItems?: ReactNode
}

const EventActivityHeaderContextMenu = ({
  activity,
  activities,
  allSelected,
  trigger,
  extraItems,
}: Props) => {
  const { team } = useTeam()
  const { eventId } = useParams()
  const {
    updateActivitySelectionForAllParticipants,
    updateEventActivity,
    copySelectionsFromActivity,
    removeEventActivity,
  } = useTeamEventUpdateContext()
  const numericEventId = Number(eventId)
  const [isOpen, setIsOpen] = useState(false)
  const [copyingToActivityId, setCopyingToActivityId] = useState<number | null>(
    null,
  )
  const [importingFromActivityId, setImportingFromActivityId] = useState<
    number | null
  >(null)
  const [isUpdatingActivity, setIsUpdatingActivity] = useState(false)
  const [isRemovingActivity, setIsRemovingActivity] = useState(false)
  const copyTargetActivities = useMemo(
    () => activities.filter((candidate) => candidate.id !== activity.id),
    [activities, activity.id],
  )
  const { data: previousSetupsResponse, isFetching: isLoadingPreviousSetups } =
    useQuery({
      queryKey: [
        'eventActivityPreviousSetups',
        {
          teamId: team.id,
          eventId: numericEventId,
          activityId: activity.id,
        },
      ],
      enabled: isOpen && Number.isFinite(numericEventId),
      staleTime: 60_000,
      queryFn: async () =>
        await getEventActivityPreviousSetups(
          team.id,
          numericEventId,
          activity.id,
        ),
    })
  const previousSetups = previousSetupsResponse?.previousSetups ?? []
  const hasPendingSelectionCopy =
    !!copyingToActivityId || !!importingFromActivityId
  const hasPendingAction =
    hasPendingSelectionCopy || isUpdatingActivity || isRemovingActivity
  const canRemoveActivity = activities.length > 1

  const handleCopySelections = async (targetActivityId: number) => {
    setCopyingToActivityId(targetActivityId)
    try {
      await copySelectionsFromActivity(targetActivityId, activity.id)
    } finally {
      setCopyingToActivityId(null)
    }
  }
  const handleImportSelections = async (setup: EventActivityPreviousSetup) => {
    setImportingFromActivityId(setup.activityId)
    try {
      await copySelectionsFromActivity(
        activity.id,
        setup.activityId,
        setup.event.id,
      )

      toast.success('Selections imported successfully')
    } finally {
      setImportingFromActivityId(null)
      setIsOpen(false)
    }
  }
  const handleUpdateActivity = async () => {
    setIsUpdatingActivity(true)
    try {
      await updateEventActivity(activity.id, { active: !activity.active })
      setIsOpen(false)
    } finally {
      setIsUpdatingActivity(false)
    }
  }
  const handleRemoveActivity = async () => {
    setIsRemovingActivity(true)
    try {
      await removeEventActivity(activity.id)
      setIsOpen(false)
    } finally {
      setIsRemovingActivity(false)
    }
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (hasPendingAction) return
        setIsOpen(nextOpen)
      }}
    >
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DropdownMenuLabel>
          <div className='flex items-center gap-2'>
            {activity.encounter?.thumbnailUrl || activity.instance?.tileUrl ? (
              <img
                className='h-8 w-8 rounded-md border border-border shrink-0'
                src={
                  activity.encounter?.thumbnailUrl ??
                  activity.instance?.tileUrl ??
                  ''
                }
              />
            ) : (
              <div className='h-8 w-8 rounded-md border border-border bg-muted shrink-0' />
            )}

            <div className='min-w-0'>
              <div className='truncate'>
                {activity.encounter?.name || activity.instance?.name || '-'}
              </div>
              <div className='text-muted-foreground text-xs font-normal'>
                {(activity.encounter
                  ? activity.instance?.name
                  : difficultiesLabels[activity.difficulty]) || '-'}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={
              copyTargetActivities.length === 0 ||
              hasPendingAction ||
              !activity.active
            }
          >
            Copy setup to
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {copyTargetActivities.map((targetActivity) => {
                const isCopying = copyingToActivityId === targetActivity.id
                return (
                  <DropdownMenuItem
                    key={targetActivity.id}
                    disabled={hasPendingAction || !targetActivity.active}
                    onSelect={(event) => {
                      event.preventDefault()
                      if (hasPendingAction) return
                      void handleCopySelections(targetActivity.id)
                    }}
                  >
                    <span className='flex w-full items-center gap-2'>
                      {isCopying ? (
                        <Loader2 className='h-8 w-8 animate-spin shrink-0 text-muted-foreground' />
                      ) : targetActivity.encounter?.thumbnailUrl ||
                        targetActivity.instance?.tileUrl ? (
                        <img
                          className={cn(
                            'h-8 w-8 rounded border border-border shrink-0',
                            !targetActivity.active && 'grayscale',
                          )}
                          src={
                            targetActivity.encounter?.thumbnailUrl ??
                            targetActivity.instance?.tileUrl ??
                            ''
                          }
                        />
                      ) : (
                        <span
                          className={cn(
                            'h-8 w-8 rounded border border-border bg-muted shrink-0',
                            !targetActivity.active && 'grayscale',
                          )}
                        />
                      )}
                      <span className='min-w-0 flex flex-col'>
                        <span className='truncate'>
                          {targetActivity.encounter?.name ||
                            targetActivity.instance?.name ||
                            '-'}
                        </span>
                        <span className='text-xs text-muted-foreground truncate'>
                          {targetActivity.encounter
                            ? `${targetActivity.instance?.name}, `
                            : null}
                          {difficultiesLabels[targetActivity.difficulty]}
                        </span>
                      </span>
                    </span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={hasPendingAction || !activity.active}
          >
            Import setup from
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {isLoadingPreviousSetups ? (
                <DropdownMenuItem disabled>
                  <span className='flex items-center gap-2'>
                    <Loader2 className='h-4 w-4 animate-spin shrink-0 text-muted-foreground' />
                    Loading setups
                  </span>
                </DropdownMenuItem>
              ) : previousSetups.length === 0 ? (
                <DropdownMenuItem disabled>No previous setups</DropdownMenuItem>
              ) : (
                previousSetups.map((setup) => {
                  const isImporting =
                    importingFromActivityId === setup.activityId
                  return (
                    <DropdownMenuItem
                      key={`${setup.event.id}:${setup.activityId}`}
                      disabled={hasPendingAction}
                      onSelect={(event) => {
                        event.preventDefault()
                        if (hasPendingAction) return
                        void handleImportSelections(setup)
                      }}
                    >
                      <span className='flex w-full items-center gap-2'>
                        {isImporting ? (
                          <Loader2 className='h-8 w-8 animate-spin shrink-0 text-muted-foreground' />
                        ) : activity.encounter?.thumbnailUrl ? (
                          <img
                            className='h-8 w-8 rounded-md border border-border shrink-0'
                            src={activity.encounter.thumbnailUrl}
                          />
                        ) : (
                          <div className='h-8 w-8 rounded-md border border-border bg-muted shrink-0' />
                        )}
                        <span className='min-w-0 flex flex-col w-full'>
                          <span className='truncate'>
                            {dayjs(setup.event.date).format('ddd, MMM D YYYY')}
                          </span>
                          <div className='flex items-center justify-between gap-2 w-full'>
                            <span className='text-xs text-muted-foreground'>
                              {setup.totalParticipants} selected
                            </span>

                            <LinkButton
                              to={`${team.path}/events/${setup.event.id}`}
                              target='_blank'
                              rel='noreferrer'
                              size='xs'
                              onClick={(event) => {
                                event.stopPropagation()
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation()
                              }}
                            >
                              <Link size={12} />
                            </LinkButton>
                          </div>
                        </span>
                      </span>
                    </DropdownMenuItem>
                  )
                })
              )}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          disabled={!activity.active || hasPendingAction}
          onClick={() => {
            updateActivitySelectionForAllParticipants(activity.id, !allSelected)
          }}
        >
          {allSelected ? 'Bench all participants' : 'Select all participants'}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={hasPendingAction}
          onSelect={(event) => {
            event.preventDefault()
            if (hasPendingAction) return
            void handleUpdateActivity()
          }}
        >
          <div className='flex items-center justify-between gap-2 w-full'>
            {activity.active ? 'Set inactive' : 'Set active'}
            {isUpdatingActivity ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
            ) : null}
          </div>
        </DropdownMenuItem>

        {extraItems ? (
          <>
            <DropdownMenuSeparator />
            {extraItems}
          </>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className='text-destructive-foreground'
          disabled={!canRemoveActivity || hasPendingAction}
          onSelect={(event) => {
            event.preventDefault()
            if (!canRemoveActivity || hasPendingAction) return
            void handleRemoveActivity()
          }}
        >
          <div className='flex items-center justify-between gap-2 w-full'>
            Remove
            {isRemovingActivity ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
            ) : null}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EventActivityHeaderContextMenu
